import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  type AddToCampaignResult,
  type SalesLead,
  useAddLeadToInstantlyCampaign,
  useInstantlyCampaigns,
} from '@/models/salesLead'

interface AddToCampaignDialogProps {
  lead: SalesLead
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddToCampaignDialog({ lead, open, onOpenChange }: AddToCampaignDialogProps) {
  const [search, setSearch] = useState('')
  const [selectedCampaignId, setSelectedCampaignId] = useState('')
  const { data: campaigns = [], isLoading } = useInstantlyCampaigns(search || undefined, open)
  const addMutation = useAddLeadToInstantlyCampaign()

  function handleAdd() {
    if (!selectedCampaignId) return
    addMutation.mutate(
      { leadId: lead.id, campaignId: selectedCampaignId },
      {
        onSuccess: (result: AddToCampaignResult) => {
          if (result.leadsUploaded > 0) {
            toast.success('Lead added to campaign')
          } else if (result.skippedCount > 0) {
            toast.info('Lead is already in this campaign')
          } else {
            toast.warning('Lead was not added — the campaign may be inactive or the email may be suppressed')
          }
          onOpenChange(false)
          setSelectedCampaignId('')
          setSearch('')
        },
        onError: () => {
          toast.error('Failed to add lead to campaign')
        },
      }
    )
  }

  function handleOpenChange(value: boolean) {
    if (!value) {
      setSelectedCampaignId('')
      setSearch('')
    }
    onOpenChange(value)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Add to Instantly Campaign</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="campaign-search">Search campaigns</Label>
            <Input
              id="campaign-search"
              placeholder="Search by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Campaign</Label>
            <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId} disabled={isLoading}>
              <SelectTrigger>
                <SelectValue placeholder={isLoading ? 'Loading…' : 'Select a campaign'} />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
                {!isLoading && campaigns.length === 0 && (
                  <div className="text-muted-foreground px-3 py-2 text-sm">No campaigns found</div>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!selectedCampaignId || addMutation.isPending}
            loading={addMutation.isPending ? 'Adding…' : undefined}
          >
            Add to Campaign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
